import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send, Square } from "lucide-react";

interface ChatInputProps {
  onSend: (message: string) => void;
  onStop?: () => void;
  isStreaming?: boolean;
  isCancelling?: boolean;
  isReady?: boolean;
  placeholder?: string;
}

const ChatInput = ({
  onSend,
  onStop,
  isStreaming = false,
  isCancelling = false,
  isReady = true,
  placeholder = "Ask me anything about golf recruiting...",
}: ChatInputProps) => {
  const [message, setMessage] = useState("");

  const handleSend = () => {
    if (!isReady) return;
    if (message.trim()) {
      onSend(message);
      setMessage("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="border-t border-border bg-card px-6 pt-4 pb-[calc(env(safe-area-inset-bottom)+1rem)]">
      <div className="max-w-4xl mx-auto flex gap-3 items-end">
        <Textarea
          value={message}
          onChange={e => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            !isReady
              ? placeholder || "Preparing chat..."
              : isCancelling
                ? "Stopping previous message..."
                : placeholder
          }
          className="min-h-[60px] resize-none bg-background border-border text-base md:text-sm"
          rows={2}
          disabled={isStreaming || isCancelling || !isReady}
        />
        {isStreaming ? (
          <Button
            onClick={onStop}
            className="bg-destructive hover:bg-destructive/90 text-destructive-foreground h-[60px] px-6"
            title="Stop generating"
            disabled={isCancelling}
          >
            <Square className="h-5 w-5" />
          </Button>
        ) : (
          <Button
            onClick={handleSend}
            className="bg-primary hover:bg-primary/90 text-primary-foreground h-[60px] px-6"
            disabled={!isReady || !message.trim() || isCancelling}
          >
            <Send className="h-5 w-5" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default ChatInput;
